<template>
  <WidgetFrame @onResize="onResize">
    <template v-slot:title>
      {{ title }}
    </template>
    <template v-slot:content>
      <div class='controls'>
        <span class="timeline-filter-flex-grow-container">
          <BadgeFilter :filters="filterObject" :historyList="filterHistory" :fields="filterFields" :fieldValues="filterFieldValues" @modified="onFilterModified" @historyModified="onHistoryModified"></BadgeFilter>
        </span>
        <PriorityNavigation ulClassName="timeline-priority-nav-ul" class="timeline-priority-nav">
          <li>
            <ListFilter :term="filter" @applyFilter="applyFilter" :termOnly='true'/>
          </li>
          <li class="d-flex ml-1 mr-1 date start-date-elevation">
            <label class="ml-1 mr-1 timeline-span" for="startDate">{{ $t('label.date') }}</label>
            <b-form-datepicker v-model="startDate" class="date-picker" @input="dateChanged"
                today-button
                close-button
                hide-header
                size="sm"
                boundary="viewport"
                :popper-opts="{ positionFixed: true }"
                :label-today-button="$t('date.today')"
                :label-close-button="$t('date.close')"
                today-button-variant="primary"
                reset-button-variant="danger" 
                close-button-variant="secondary"
              >
                <template v-slot:button-content="{ }">
                  <font-awesome-icon :icon="['far', 'calendar-days']" />
                </template>
              </b-form-datepicker>
          </li>
          <li class="d-flex mr-1 timescale-action">
            <label class="ml-1 mr-1 timeline-span" for="timescale">{{ $t('staff.timescale') }}</label>
            <multiselect v-model="span" class="custom-dropdown-options enable-option-icon fit-content-fix"
              :max-height="300"
              :options="spanOptions.map(i => i.value)"
              :custom-label="getSpanOptionLabel"
              :placeholder="''"
              :searchable="false" 
              :allow-empty="false"
              :showLabels="false">
              <template slot="option" slot-scope="props">
                <font-awesome-icon class="selected-option-icon" v-if="span == props.option" :icon="['far', 'check']" />
                <span class="option__title">{{ getSpanOptionLabel(props.option) }}</span>
              </template>
            </multiselect>
          </li>
          <li class="view no-left-padding timeline-buttons" @[viewMouseEnterEvent]="onViewOver" @mouseleave="onViewLeave">
            <b-dropdown :id="`BTN_VIEW_TIMELINE`" ref="view" class="view-btn action-bar-dropdown" toggle-class="text-decoration-none" no-caret>
              <template #button-content>
                <font-awesome-icon :icon="['far','desktop']"/>
              </template>
              <b-dropdown-item @click="savePreset" href="#">
                <span class="action-item-label">{{ $t('project.button.save_timeline') }}</span>
              </b-dropdown-item>
              <b-dropdown-divider/>
              <template v-for="(item, index) in timelineViews">
                <b-dropdown-item class="action-item" @click="loadViewSettings(item)" href="#" :key="index">
                  <span :id="`viewname_ht${index}`" class="action-item-label-with-icon">{{ item.name }}</span>
                  <b-popover
                    v-if="isEllipsisActive(item.name, `viewname_ht${index}`)"
                    :target="`viewname_ht${index}`"
                    placement="top"
                    boundary="viewport"
                    custom-class="popover-margin"
                    triggers="hover"
                    offset="-100"
                    :content="item.name">
                  </b-popover> 
                  <span>
                    <span class="action-item-icon" 
                        v-if="!editPermission(item)"
                        :id="`BTN_VIEW_TIMELINE_COPY_${index}`"
                        @click.stop.prevent="copyColumnSettings(item.name, item)">
                      <font-awesome-icon class="" :icon="['far','copy']"/>
                    </span>
                    <b-popover
                      v-if="!editPermission(item)"
                      :target="`BTN_VIEW_TIMELINE_COPY_${index}`"
                      placement="top"
                      boundary="viewport"
                      triggers="hover"
                      :content="$t('project.button.copy_timeline')">
                    </b-popover>
                    <span class="action-item-icon position-third" 
                        v-if="!editPermission(item)"
                        @[infoMouseEnterEvent]="onInfoOver(index)" @mouseleave="onInfoLeave(index)"
                        :id="`BTN_TIMELINE_INFO_${index}`">
                      <font-awesome-icon class="" :icon="['far','circle-info']"/>
                    </span>
                    <b-popover
                      v-if="!editPermission(item)"
                      :target="`BTN_TIMELINE_INFO_${index}`"
                      :ref="`BTN_TIMELINE_INFO_${index}`"
                      :show.sync="showInfo[index]"
                      placement="top"
                      boundary="viewport"
                      triggers="manual"
                      :content="$t('owner', [timelineViews[index].owner])">
                    </b-popover>
                    <span class="action-item-icon position-third" 
                        v-if="editPermission(item)"
                        :id="`BTN_VIEW_TIMELINE_SHARE_${index}`"
                        @click.stop.prevent="shareColumnSettings(index, item.name, item)">
                      <font-awesome-icon class="" :icon="[item.defaultView ? 'fas' : 'far','share-nodes']"/>
                    </span>
                    <b-popover
                      v-if="editPermission(item)"
                      :target="`BTN_VIEW_TIMELINE_SHARE_${index}`"
                      placement="top"
                      boundary="viewport"
                      triggers="hover"
                      :content="$t('project.button.share_timeline')">
                    </b-popover>
                    <span class="action-item-icon position-second" 
                        v-if="editPermission(item)"
                        :id="`BTN_VIEW_TIMELINE_UPDATE_${index}`"
                        @click.stop.prevent="updateColumnSettings(index, item.name, item)">
                      <font-awesome-icon class="" :icon="['far','save']"/>
                    </span>
                    <b-popover
                      v-if="editPermission(item)"
                      :target="`BTN_VIEW_TIMELINE_UPDATE_${index}`"
                      placement="top"
                      boundary="viewport"
                      triggers="hover"
                      :content="$t('project.button.update_timeline')">
                    </b-popover>
                    <span class="action-item-icon"
                        v-if="editPermission(item)"
                        :id="`BTN_VIEW_TIMELINE_DELETE_${index}`"
                        @click.stop.prevent="removeColumnSettings(index)">
                      <font-awesome-icon class="" :icon="['far','trash-alt']"/>
                    </span>
                    <b-popover
                      v-if="editPermission(item)"
                      :target="`BTN_VIEW_TIMELINE_DELETE_${index}`"
                      placement="top"
                      boundary="viewport"
                      triggers="hover"
                      :content="$t('project.button.delete_timeline')">
                    </b-popover>
                  </span>
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </li>
          <li @[colorMouseEnterEvent]="onColoringOver" @mouseleave="onColoringLeave">
            <b-dropdown :id="`BTN_COLORING_TIMELINE`" ref="coloring" class="no-left-padding timeline-buttons action-bar-dropdown" toggle-class="text-decoration-none" no-caret>
              <template #button-content>
                <font-awesome-icon :icon="['far', 'palette']"/>
              </template>
              <b-dropdown-group :header="$t('colorby')">
                <b-dropdown-item @click="onColorChange('none', 'coloring')" href="#">
                  <span class="action-item-label">{{ $t('none') }}</span><font-awesome-icon class="active-check" v-if="display.coloring.none" :icon="['far', 'check']"/>
                </b-dropdown-item>
                <b-dropdown-item @click="onColorChange('location', 'coloring')" href="#">
                  <span class="action-item-label">{{ $t('staff.coloring.location') }}</span><font-awesome-icon class="active-check" v-if="display.coloring.location" :icon="['far', 'check']"/>
                </b-dropdown-item>
                <b-dropdown-item @click="onColorChange('stage', 'coloring')" href="#">
                  <span class="action-item-label">{{ $t('project.display.status') }}</span><font-awesome-icon class="active-check" v-if="display.coloring.stage" :icon="['far', 'check']"/>
                </b-dropdown-item>
                <b-dropdown-item @click="onColorChange('project', 'coloring')" href="#">
                  <span class="action-item-label">{{ $t('staff.coloring.project') }}</span><font-awesome-icon class="active-check" v-if="display.coloring.project" :icon="['far', 'check']"/>
                </b-dropdown-item>
                <b-dropdown-item @click="onColorChange('customer', 'coloring')" href="#">
                  <span class="action-item-label">{{ $t('staff.coloring.customer') }}</span><font-awesome-icon class="active-check" v-if="display.coloring.customer" :icon="['far', 'check']"/>
                </b-dropdown-item>
              </b-dropdown-group>
            </b-dropdown>
          </li>
        </PriorityNavigation>
        <div class="timeline-attribute-toggler">
          <b-dropdown dropleft id="ddown-offset" :popper-opts="{ positionFixed: true }" variant="link"  class="timeline-buttons" no-caret boundary="viewport" :offset="sizeClass['SMH'] ? '-52' : '0'">
            <template slot="button-content">
              <div class="text">
                <font-awesome-icon :icon="['far', 'ellipsis-vertical']"/>
              </div>
            </template>
            <b-dropdown-group :header="$t('project.display.title')">
              <template v-for="(item, key) in displayList">
                  <div :key="key" class="dropdown-item" @click.prevent>
                    <b-form-checkbox v-model="display[key]" @click.native="toggleVisible(key)">{{displayLabels[key]}}</b-form-checkbox>
                  </div>
              </template>
            </b-dropdown-group>
            <b-dropdown-group :header="$t('project.progress.title')">
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress0']" @click.native="toggleVisible('_progress0')">
                  <span class='legend-item project-tile-0'></span>
                  <span>0%</span>
                </b-form-checkbox>
              </div>
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress25']" @click.native="toggleVisible('_progress25')">
                  <span class='legend-item project-tile-25'></span>
                  <span>1% - 25%</span>
                </b-form-checkbox>
              </div>
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress50']" @click.native="toggleVisible('_progress50')">
                  <span class='legend-item project-tile-50'></span>
                  <span>26% - 50%</span>
                </b-form-checkbox>
              </div>
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress75']" @click.native="toggleVisible('_progress75')">
                  <span class='legend-item project-tile-75'></span>
                  <span>51% - 75%</span>
                </b-form-checkbox>
              </div>
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress99']" @click.native="toggleVisible('_progress99')">
                  <span class='legend-item project-tile-99'></span>
                  <span>76% - 99%</span>
                </b-form-checkbox>
              </div>
              <div class="dropdown-item progress-toggle" @click.prevent>
                <b-form-checkbox v-model="display['_progress100']" @click.native="toggleVisible('_progress100')">
                  <span class='legend-item project-tile-100'></span>
                  <span>100%</span>
                </b-form-checkbox>
              </div>
            </b-dropdown-group>
          </b-dropdown>
          <b-popover target="ddown-offset" triggers="hover" placement="top" boundary="viewport">
            {{ $t('project.settings.display') }}
          </b-popover>
        </div>
      </div>
      <div v-if="!canView('PROJECT', ['scheduleStart', 'scheduleFinish'])" class="center-text">
        {{ $t('entity_selector.error.insufficient_permission_to_show_data') }}
      </div>
      <div v-else id="timeline-wrapper">
        <table class="timeline-table" >
          <colgroup>
            <col v-for="month in months" :key="`colgroup-${span}-${month.date}`" :class="applyHeaderClass(month)"/>
          </colgroup>
          <thead>
            <tr>
              <th v-for="month in months" :key="`th-${span}-${month.date}`" :class="month.isToday ? 'todayHead' : ''">{{month.date}}</th>
            </tr>
          </thead>
          <tr v-for="(column, index) in rows" :key="index">
            <template v-for="cell in column">
              <template v-if="cell.project">
                <td v-bind:key="`project-cell-${span}-${cell.date}-${cell.project.uuId}`" :colspan="cell.span">
                  <a
                      :id="'timeline-'+cell.project.uuId"
                      v-on:click="projectId = cell.project.uuId; renderPopup($event)"
                      tabindex="0"
                      :class="['project-bar', getColorClass(cell.project.progress), {'no-start': cell.noStart, 'no-end': cell.noEnd}]"
                      :style="{
                        background: getOuterColor(cell.project.progress, cell.project),
                        borderColor: getOuterColor(cell.project.progress, cell.project),
                      }"
                      >
                    <span class="project-name" :style="{ background: getInnerColor(cell.project.progress, cell.project), color: getTextColor(cell.project)}">
                      <v-clamp autoresize :max-lines="1">
                        {{ display['customer'] && cell.project.customer ? cell.project.customer + ' • ' : ''}}
                        {{cell.project.name}}
                        {{ display['status'] && cell.project.status ? ' • [' + cell.project.status + ']' : ''}}
                      </v-clamp>
                    </span>
                    <span class="progress-box" v-if="display['progress']">
                      <span v-if="cell.progress == 0">
                        <i class="far fa-minus"></i>
                      </span>
                      <span v-else-if="cell.progress == 100">
                        <i class="far fa-check"></i>
                      </span>
                      <span v-else>
                        {{cell.progress}}%
                      </span>
                    </span>
                  </a>
                  <span class='milestone-container' v-if="display['milestones']">
                    <span v-for="(m, index) in cell.milestones"
                      :key="index"
                      :id="'timeline-milestone-'+m.id"
                      class='timeline-milestone'
                      :style="{
                          left: m.position+'%',
                          borderBottomColor: getNotchColor(cell.project.progress, cell.project)
                        }"
                      >
                      <b-popover
                        :target="'timeline-milestone-'+m.id"
                        :custom-class="'milestone timeline-tooltip ' + getColorClass(cell.project.progress)"
                        triggers="hover"
                        placement="bottom"
                        boundary="viewport"
                        >
                        <div class="timeline-tooltip-header milestone-tooltip-header">
                          <div class='diamond'><font-awesome-icon :icon="['fas','diamond']" /></div>
                          <div class='milestone-title'>
                            <div class='milestone-word'>Milestone:</div>
                            <div class='milestone-name'>{{ m.name }}</div>
                          </div>
                        </div>
                        <div class="timeline-tooltip-body">
                          <div class="table">
                              <span class="title">Date</span>
                              <span class="content">
                                {{ m.date }}
                              </span>
                          </div>
                          <div class="table">
                              <span class="title">Progress</span>
                              <span class="content">
                                {{ m.progress ? (m.progress * 100).toFixed(0) : 0 }}%
                              </span>
                          </div>
                        </div>
                      </b-popover>
                    </span>
                  </span>
                  <b-popover
                    :target="'timeline-'+cell.project.uuId"
                    :custom-class="'timeline-tooltip ' + getColorClass(cell.project.progress)"
                    triggers="hover"
                    placement="top"
                    boundary="viewport"
                    >
                    <div class="timeline-tooltip-header milestone-tooltip-header">
                      <div class='diamond'><font-awesome-icon :icon="['far', 'chart-network']"/></div>
                      <div class='milestone-title'>
                        <div class='milestone-word'>{{ $t('project.title') }}:</div>
                        <div class='milestone-name'>{{ cell.project.name }}</div>
                      </div>
                    </div>
                    <div class="timeline-tooltip-body">
                      <div class="customer">
                          <div class="title">
                              {{ $t('customer.title') }}
                          </div>
                          <div class="content">
                              {{ cell.project.customer ? cell.project.customer : $t('customer.no_customer') }}
                          </div>
                      </div>
                      
                      <div class="table">
                          <span class="title">{{ $t('start') }}</span>
                          <span class="title">{{ $t('end') }}</span>
                          <span class="content">
                            {{ cell.project.startTime > 0 ? moment(cell.project.startTime).format('LL') : '' }}
                          </span>
                          <span class="content">
                            {{ cell.project.closeTime > 0 ? moment(cell.project.closeTime).format('LL') : '' }}
                          </span>
                      </div>
                      <div class="table">
                          <span class="title">{{ $t('project.field.budget') }}</span>
                          <span class="title">{{ $t('project.field.actual') }}</span>
                          <span class="content">
                            {{costFormat(cell.project.fixedCost ? cell.project.fixedCost : cell.project.estimatedCost, cell.project.currencyCode)}}
                          </span>
                          <span class="content">
                            {{costFormat(cell.project.actualCost, cell.project.currencyCode)}}
                          </span>
                      </div>
                      <div class="table">
                          <span class="title">{{ $t('project.display.progress') }}</span>
                          <span class="title">{{ $t('project.field.status') }}</span>
                          <span class="content">
                            {{ (cell.project.progress * 100).toFixed(0) }}%
                          </span>
                          <span class="content">
                            {{ cell.project.status }}
                          </span>
                      </div>
                    </div>
                  </b-popover>
                </td>
              </template>
              <template v-else>
                <td v-bind:key="`empty-cell-${span}-${cell.date}`" :colspan="cell.span"></td>
              </template>
            </template>
          </tr>
        </table>
        <SaveViewModal :show.sync="promptSaveShow" :name="saveName" :title="$t('task.confirmation.save')" :profile="saveProfile" @ok="confirmSaveOk"/>
        <SaveViewModal :show.sync="promptShareShow" :name="saveName" :title="$t('task.confirmation.share')" :sharing="true" :profile="saveProfile" @ok="confirmSaveOk"/> 
        
        <b-modal :title="$t('task.confirmation.title_delete')"
            v-model="confirmDeleteViewShow"
            @ok="confirmDeleteViewOk"
            content-class="shadow"
            no-close-on-backdrop
            >
          <div class="d-block">
            {{ $t('task.confirmation.delete_view') }}
          </div>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="success" @click="ok()">{{ $t('button.confirm') }}</b-button>
            <b-button size="sm" variant="danger" @click="cancel()">{{ $t('button.cancel') }}</b-button>
          </template>
        </b-modal>
        
      </div>
    </template>
  </WidgetFrame>
</template>

<script>
import * as moment from 'moment-timezone';
moment.tz.setDefault('Etc/UTC');
const locale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
moment.locale(locale);
import { projectService, viewProfileService, companyService, dashboardProfileService,
         profileService } from '@/services';
import VClamp from 'vue-clamp';
import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { costFormatAdv, invertColor, toComplimentary, isEllipsisActive } from '@/helpers';
import ListFilter from '@/components/ListFilter/ListFilter';
import BadgeFilter from '@/components/Filter/BadgeFilter';
import PriorityNavigation from '@/components/PriorityNavigation/PriorityNavigation';
import Multiselect from 'vue-multiselect';
/**
 * Note: this widget's initial data load and draw happens in the resize function
 * and not Vue's lifecycle functions because we need to know how wide the widget
 * is to determine the initial number of months to load.
 * 
 * Note: cell width (and calculations) is hard-coded to 80. There's potential to
 * make it responsive in the future.
 */
export default {
  name: 'HomeTimelineWidget',
  components: {
    WidgetFrame: () => import('@/components/Dashboard/WidgetFrame'),
    VClamp,
    ListFilter,
    SaveViewModal: () => import('@/components/modal/SaveViewModal.vue'),
    BadgeFilter,
    PriorityNavigation,
    Multiselect
  },
  props: {
    profile:    { type: Object, required: true },
    refresh:    { type: Boolean, default: false }
  },
  data: function() {
    return {
      orientation: null,
      width: 0,
      height: 0,
      firstLoadDone: false,
      monthsPerLoad: 1, // the amount of months we can fit on the visible parts of the widget
      canInteract: false, // avoid scroll triggers until draw finished
      currentMonth: null,
      lowestMonth: null,
      highestMonth: null,
      startDate: null,
      projects: [],
      months: [],
      rows: [],
      fetching: false,  // loading indicator when fetching more data
      fetchDirection: null, // do we scroll left or right after fetch
      widthBeforeFetch: 0,  // helps us figure out the scroll position after fetch
      filter: '',  // search term
      display: {
        customer: false,
        milestones: true,
        progress: true,
        status: true,
        _progress0: true,
        _progress25: true,
        _progress50: true,
        _progress75: true,
        _progress99: true,
        _progress100: true,
        coloring: {
          none: true,
          project: false,
          customer: false,
          status: false,
          location: false
        }
      },
      displayLabels: {
        customer: this.$i18n.t("project.display.customer"),
        milestones: this.$i18n.t("project.display.milestones"),
        progress: this.$i18n.t("project.display.progress"),
        status: this.$i18n.t("project.display.status"),
      },
      hasMounted: false,
      mouseX: null,
      span: "Monthly",
      spanOptions: [
        { text: "Day", value: "Daily" },
        { text: "Week", value: "Weekly" },
        { text: "Month", value: "Monthly" },
        { text: "Year", value: "Yearly" }
      ],
      
      filterObject: [],
      filterFields: [],
      filterFieldValues: {}, 
      filterHistory: [],
      
      promptSaveShow: false,
      promptShareShow: false,
      saveName: null,
      saveProfile: null,
      saveIndex: -1,
      confirmDeleteViewShow: false,
      deleteViewIndex: -1,
      timelineViews: [],
      projectId: null,
      viewName: null,
      showInfo: []
    }
  },
  watch: {
    months: function() {
      var self = this;
      Vue.nextTick(function () {
        if (self.fetchDirection == "left") {
          self.scrollFromLeft();
        }
      });
    },
    span(newValue) {
      this.profile.span = this.span;
      this.projects = [];
      this.$emit('saveWidget', this.profile);
      this.firstLoadSetup();
    },
    refresh(newValue) {
      this.projects = [];
      this.firstLoadSetup();
      this.$emit('refreshed');
    }
  },
  mounted() {
    this.hasMounted = true;
  },
  updated() {
    if (!this.firstLoadDone) {
      this.firstLoadSetup();
    }
  },
  created() {
    this.userId = this.$store.state.authentication.user.uuId;
    this.spanOptions[0].text = this.$t('timescale.day');
    this.spanOptions[1].text = this.$t('timescale.week');
    this.spanOptions[2].text = this.$t('timescale.month');
    this.spanOptions[3].text = this.$t('timescale.year');
    
    // Apply all the saved display settings
    if (this.profile.display) {
      let key = null;
      for (key in this.profile.display) {
        // eslint-disable-next-line
        if (this.display.hasOwnProperty(key)) {
          this.display[key] = this.profile.display[key];
        }
      }
    }

    if (this.profile.viewName) {
      this.viewName = this.profile.viewName;
    }
    
    if (this.profile.filter) {
      this.filter = this.profile.filter;
    }
    
    if (this.profile.span) {
      this.span = this.profile.span;
    }
    
    if (this.profile.startDate) {
      this.startDate = new Date(this.profile.startDate);
    }
    else {
      this.startDate = new Date();
    }
    
    this.loadProfile();
    this.loadPublicProfile(); // Public profile holds public Timeline views
    
    function getMatrix(element) {
      const values = element.style.transform.split(/\w+\(|\);?/);
      const transform = values[1].split(/,\s?/g).map(n => parseInt(n));

      return {
        x: transform[0],
        y: transform[1],
        z: transform[2]
      };
    }

    window.App.$root.$on('bv::popover::shown', (ev) => {
      if (!ev.target.classList.contains('project-bar')) {
        // Not one of ours. Ignore it.
        return true;
      }
      var popup = ev.relatedTarget;

      // Reposition the popup to the center of the mouse position at
      // the point of entry we saved earlier
      
      var coords = getMatrix(popup);
      var pRect = popup.getBoundingClientRect();
      var x = this.mouseX - (pRect.width/2)+2; // 2 pixels for border?
      var y = coords.y;
      if (!x) {
        x = coords.x;
      }

      popup.style.setProperty('transform', 'translate3d(' +x+'px, '+y+'px, 0px)');
      popup.style.setProperty('visibility', 'visible');
    })
    // The plugin translates again during hiding so we have to ensure we
    // hide it before or it will move around.
    window.App.$root.$on('bv::popover::hide', (ev) => {
      if (!ev.target.classList.contains('project-bar')) {
        return;
      }
      var popup = ev.relatedTarget;
      if (popup.style) {
        popup.style.setProperty('visibility', 'hidden');
      }
    });
  },
  computed: {         
    infoMouseEnterEvent() {
      return this.isTouchDevice()? null : 'mouseenter';
    },
    title() {
      if (this.viewName) {
        return `${this.$t('permission.timeline')} - ${this.viewName}`;
      }
      return this.$t('permission.timeline');
    },
    viewMouseEnterEvent() {
      return this.isTouchDevice()? null : 'mouseenter';
    },
    dateUnit() {
      if (this.span === 'Daily') {
        return 'days';
      }
      if (this.span === 'Weekly') {
        return 'weeks';
      }
      if (this.span === 'Monthly') {
        return 'months';
      }
      if (this.span === 'Yearly') {
        return 'years';
      }
      return 'months';
    },
    spanUnit() {
      if (this.span === 'Daily') {
        return 'day';
      }
      if (this.span === 'Weekly') {
        return 'week';
      }
      if (this.span === 'Monthly') {
        return 'month';
      }
      if (this.span === 'Yearly') {
        return 'year';
      }
      return 'month';
    },
    colorMouseEnterEvent() {
      return this.isTouchDevice()? null : 'mouseenter';
    },
    companyrule() {
      if (this.$store.state.company && this.$store.state.company.type !== 'Primary' &&
          this.$store.state.company.filterIds) {
        const companies = this.$store.state.company.filterIds;
        const companyrule = ['PROJECT.COMPANY.uuId', 'within', companies.join('|')];
        return companyrule
      }
      return null;
    },
    filteredProjects() {
      var projects = this.projects
      const self = this;
      if (this.filter) {
        projects = projects.filter((p) => {
          return (
            p.name.toLowerCase().includes(self.filter.toLowerCase()) ||
            (p.description && p.description.toLowerCase().includes(self.filter.toLowerCase())) ||
            (p.customer && p.customer.toLowerCase().includes(self.filter.toLowerCase())) ||
            (p.status && p.status.toLowerCase().includes(self.filter.toLowerCase())) ||
            (p.location && p.location.length > 0 && p.location[0].toLowerCase().includes(self.filter.toLowerCase())) ||
            (p.tag && p.tag.filter(t => t.toLowerCase().includes(self.filter.toLowerCase())).length !== 0)
          )
        })
      }
      
      if (this.filterObject.length !== 0) {
        projects = projects.filter(p => {
          return this.filterObject.filter(f => {
            if (f.field === 'customerName') {
              return f.value.findIndex(v => p.customer === v.text) !== -1;
            }
            else if (f.field === 'projectName') {
              return f.value.findIndex(v => p.name === v.text) !== -1;
            }
            else if (f.field === 'stageName') {
              return f.value.findIndex(v => p.status === v.text) !== -1;
            }
            else if (f.field === 'tag') {
              return f.value.findIndex(v => p.tag.includes(v.text)) !== -1;
            }
          }).length === this.filterObject.length;
        });
      }
      
      projects = projects.filter((p) => {
        var progress = (p.progress * 100).toFixed(0);
        if (progress == 0 && this.display['_progress0']) {
          return true;
        }
        if (progress >= 1 && progress <= 25 && this.display['_progress25']) {
          return true;
        }
        if (progress >= 26 && progress <= 50 && this.display['_progress50']) {
          return true;
        }
        if (progress >= 51 && progress <= 75 && this.display['_progress75']) {
          return true;
        }
        if (progress >= 76 && progress <= 99 && this.display['_progress99']) {
          return true;
        }
        if (progress == 100 && this.display['_progress100']) {
          return true;
        }
      })
      return projects
    },
    displayList() {
      return Object.keys(this.display)
        .filter(key => !key.startsWith('_progress') && key !== 'coloring')
        .reduce((obj, key) => {
          obj[key] = this.display[key];
          return obj;
        }, {})
    },
    sizeClass: function () {
      let size = {};
      if (this.height < 320) {
        size['SMH'] = true;
      }
      return size;
    },
  },
  methods: {
    async loadProfile() {
      
      const views = await this.$store.dispatch('data/presetviewProfileList', this.userId).then((value) => {
        return value;
      })
      .catch((e) => {
        console.error(e); // eslint-disable-line no-console
      });
      
      this.addViews(views);
      
    },
    async loadPublicProfile() {
      if (!localStorage.companyId) {
        const data = await companyService.list({limit: -1, start: 0}).then((response) => {
          return response.data;
        })
        .catch((e) => {
          console.error(e); // eslint-disable-line no-console
          return null;
        });

        if (data != null) {
          const company = data.filter(d => d.type === 'Primary');
          if (company.length > 0) {
            localStorage.companyId = company[0].uuId;
          }
        }
      }

      const views = await this.$store.dispatch('data/viewProfileListPublic', localStorage.companyId).then((value) => {
        return value;
      })
      .catch((e) => {
        console.error(e); // eslint-disable-line no-console
      });
      
      this.addViews(views);
    },
    dateChanged() {
      this.profile.startDate = this.startDate === moment().format('YYYY-MM-DD') ? null : this.startDate;
      this.projects = [];
      this.$emit('saveWidget', this.profile);
      this.firstLoadSetup();
    },
    isTouchDevice() {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
      const mq = function (query) {
          return window.matchMedia(query).matches;
      }
      if ('ontouchstart' in window) {
          return true;
      }
      const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
      return mq(query);
    },
    moment: function (d) {
      return moment(d);
    },
    costFormat: function(c, code) {
      return costFormatAdv(c, code);
    },
    saveMousePosition(event) {
      //console.log(event)
    },
    applyFilter(term) {
      this.filter = term;
      this.profile.filter = this.filter;
      this.$emit('saveWidget', this.profile);
      this.fillTable();
    },
    toggleVisible(key) {
      this.display[key] = !this.display[key];
      this.saveSettings();
      this.fillTable(); // Need this for progress & togglers
    },
    firstLoadSetup() {
      if (this.width === 0) {
        return;
      }
      this.firstLoadDone = true;

      this.monthsPerLoad = Math.floor(this.width / 80); // round down to account for margins of widget
      this.currentMonth = this.adjustStart(moment());
      this.lowestMonth = moment(this.startDate).clone().subtract(Math.ceil(this.monthsPerLoad/2), this.dateUnit);
      this.highestMonth = moment(this.startDate).clone().add(Math.ceil(this.monthsPerLoad/2), this.dateUnit);

      var start = this.lowestMonth.clone();
      var end = this.highestMonth.clone();
      if (this.canView('PROJECT', ['scheduleStart', 'scheduleFinish'])) {
        this.getProjectsInSpan(start, end);
      }
    },
    formatDate(month) {
      if (this.span === 'Daily') {
        return month.format('D ddd MMM YYYY');
      }
      if (this.span === 'Weekly') {
        return month.startOf('week').format('D MMM YYYY');
      }
      if (this.span === 'Monthly') {
        return month.format('MMM YYYY');
      }
      if (this.span === 'Yearly') {
        return month.format('YYYY');
      }
    },
    buildTable() {
      // Builds the table columns using the lowest and highest dates exposed
      this.months = [];
      var month = this.lowestMonth.clone();
      while (month.isSameOrBefore(this.highestMonth, this.spanUnit)) {
        this.months.push({
          date: this.formatDate(month), 
          isToday: month.isSame(this.currentMonth, this.spanUnit),
          isSelected: month.isSame(this.startDate, this.spanUnit)
        });
        month = moment(month).add(1, this.dateUnit);
      }
    },
    /**
     * start and end are both Moment objects for the target month.
     * Both must be aligned to start of month.
     */
    async getProjectsInSpan(start, end) {
      const self = this;
      const params = {start: start.valueOf(), end: end.endOf('month').valueOf(), };
      params.companyrule = this.companyrule;
      this.fetching = true;
      var projects = await projectService.listTimelineWidgetProjects(params).then((response) => {
        return response.data;
      });

      // determine the earliest and latest project date
      let earliest = null;
      let latest = null;
      for (var i = 0; i < projects.length; i++) {
        if (projects[i].startTime !== 0 &&
            (earliest === null ||
            projects[i].startTime < earliest)) {
          earliest = projects[i].startTime;  
        }
        if (projects[i].closeTime !== 0 &&
            (latest === null || 
            projects[i].closeTime > latest)) {
          latest = projects[i].closeTime;  
        }
      }
      
      if (earliest !== null && latest !== null) {
        this.lowestMonth = moment(earliest);
        this.highestMonth = moment(latest);
      }
      
      this.buildTable();
      
      Vue.nextTick(function () {
        self.scrollToToday();
        self.attachScrollers();
      });
      
      if (this.canView('TASK')) {
        var ids = projects.map(p => p.uuId)
        await projectService.listTimelineWidgetMilestones(ids).then((response) => {
          var milestones = response.data;
  
          projects.forEach(p => {
            p.milestones = [];
            milestones.forEach(m => {
              if (p.uuId == m.projectId) {
                p.milestones.push(m)
              }
            });
          })
        });
      }
      else {
        projects.forEach(p => {
          p.milestones = [];          
        })
      }
      
      self.processProjects(projects);
      self.projects = self.projects.concat(projects);
      self.setFilterFieldValues(self.projects);
      
      // Sort the whole list after combining
      self.projects.sort(function(a, b) {
        if (a.startTime < b.startTime) {
          return -1;
        }
        if (a.startTime > b.startTime) {
          return 1;
        }
        return 0;
      });

      // Only keep unique projects. It's possible for a project to
      // be fetched again because one of its start/end points falls
      // within both the old and new time spans.
      self.projects = [...new Map(self.projects.map(p => [p['uuId'], p])).values()];

      // Re-build the table with the new projects
      self.fillTable();
    },
    scrollToToday() {
      // Scroll the table so that the current month is in the center
      var today = document.querySelector('.selectedCol');
      var w = document.getElementById('timeline-wrapper');
      if (w && today) {
        w.scrollLeft = today.offsetLeft - (w.offsetWidth / 2);
      }
    },
    scrollFromLeft() {
      var w = document.getElementById('timeline-wrapper');
      w.scrollLeft = w.scrollWidth - this.widthBeforeFetch;
    },
    attachScrollers() {
      // Attach scrollers for autoload on edges
      var w = document.getElementById('timeline-wrapper');
      if (w) {
        var self = this;
        w.onscroll = function() {
          onHorizontalScroll(this);
        };
      }
      function onHorizontalScroll() {
        //self.checkTouchingEdge();
      }
    },
    attachListeners() {
      const self = this;
      function saveMouse(e) {
        self.mouseX = e.clientX;
      }
      var bars = document.getElementsByClassName('project-bar');
      for (var i = 0; i < bars.length; i++) {
          bars[i].addEventListener("mouseenter", saveMouse);
      }
    },
    checkTouchingEdge() {
      if (!this.canInteract) {
        return;
      }
      // Check if the table is scrolled fully to the left or right
      // and decide whether to load more data from that end.
      var e = document.getElementById('timeline-wrapper');
      if (e != null) {
        if (e.scrollLeft == 0) {
          this.fetchMoreFromLeft();
        } else if (e.scrollWidth - e.scrollLeft <= e.clientWidth+1) {
          // Add 1 pixel for tolerance. Sometimes the browser rounds up a subpixel.
          this.fetchMoreFromRight();
        }
      }
    },
    fetchMoreFromLeft() {
      // console.log("Load from left!");
      this.canInteract = false;
      this.fetchDirection = "left";
      this.widthBeforeFetch = document.getElementById('timeline-wrapper').scrollWidth;

      // We have scrolled to the start. Fetch projects for earlier months
      var start = this.lowestMonth.clone().subtract(6, this.dateUnit);
      var end = this.lowestMonth.clone().subtract(1, this.dateUnit);
      this.lowestMonth = start.clone();
      this.getProjectsInSpan(start, end);
    },
    fetchMoreFromRight() {
      // console.log("Load from right!");
      this.canInteract = false;
      this.fetchDirection = "right";
      this.widthBeforeFetch = document.getElementById('timeline-wrapper').scrollWidth;

      // We have scrolled to the end. Fetch projects for later months
      var start = this.highestMonth.clone().add(1, this.dateUnit);
      var end = this.highestMonth.clone().add(6, this.dateUnit);
      this.highestMonth = end.clone();
      this.getProjectsInSpan(start, end);
    },
    processProjects(projects) {
      // Add an additional attribute for start and end month of the project. Since this
      // widget only deals with months, these values will become our test for which
      // column the projects fall into (and whether they overlap).
      // 
      // If a project does not have either start or end times, we remove it from the list.
      // If a project only has one of them set, we use it for both start/end (so the project
      // consumes the minimum timeline unit of 1 month). We add an additional attribute to
      // indicate which side was assumed so we can style it later.
      
      var processed = [];
      projects.forEach(project => {
        if (!project.startTime && !project.closeTime) {
          return;
        }

        if (project.startTime) {
          project.startMonth = this.adjustStart(project.startTime);
          if (!project.closeTime) {
            project.endMonth = project.startMonth;
            project.noEnd = true;
          }
        }

        if (project.closeTime) {
          project.endMonth = this.adjustStart(project.closeTime);
          if (!project.startTime) {
            project.startMonth = project.endMonth;
            project.noStart = true;
          }
        }
        processed.push(project);
      })
      projects = processed;
    },
    adjustStart(startTime) {
      if (this.span === 'Daily') {
        return moment(startTime);
      }
      if (this.span === 'Weekly') {
        return moment(startTime).startOf('week');
      }
      if (this.span === 'Monthly') {
        return moment(startTime).startOf('month');
      }
      if (this.span === 'Yearly') {
        return moment(startTime).startOf('year');
      }
    },
    shouldAdd(p, current) {
      if (this.span === 'Daily') {
        return p.startMonth.isSame(current, 'day');
      }
      if (this.span === 'Weekly') {
        return p.startMonth.startOf('week').isSame(current, 'week');
      }
      if (this.span === 'Monthly') {
        return p.startMonth.isSame(current, 'month');
      }
      if (this.span === 'Yearly') {
        return p.startMonth.isSame(current, 'year');
      }
    },
    fillTable() {
      var self = this;
      this.buildTable();

      // Distribute the projects into rows wherever they fit. Create a new row when none
      // of the existing rows have space. Since the projects are sorted according to start
      // time, the order of the projects in each row are also sorted. So we know a project
      // fits onto that line by only checking if it starts after the end of the last project
      // on that line (i.e., we never have a case of adding a project that is between others).
      this.rows = [];
      var rows = [];
      this.filteredProjects.forEach(project => {
        var added = false;
        for (var r = 0; r < rows.length && !added; r++) {
          var row = rows[r];
          var p = row[row.length-1]; // Only need to compare last item
          if (project.startMonth.isAfter(p.endMonth)) {
            added = true;
            row.push(project);
            break;
          }
        }
        if (!added) {
          // new row for this project
          rows.push([project]);
        }
      });

      // Once we've distributed the projects in the right sequence, we build a list of 'cells'
      // to fill in the table for each row for each month. Cells indicate their size (colspan).
      // Empty cells always span 1 and project cells span 1x per month of runtime. The total
      // span for each row adds up to the number of months in the table.
      // Note that some projects may span beyond the visible range so we must truncate
      // their runtime.
      rows.forEach(row => {
        var filledRow = [];
        var current = self.lowestMonth.clone();
        while (current.isSameOrBefore(this.highestMonth, this.spanUnit)) {
          var span = 1;
          var project = null;
          var start = current.clone();
          var truncate = 0;
          var noStart = false;
          var noEnd = false;
          var progress = null;
          var milestones = []
          row.forEach(p => {
            if (self.shouldAdd(p, current)) {
              project = p;
            }
          
            // If project starts before first visible month, remove all the
            // invisible months from its span.
            if (current.isSame(this.lowestMonth, this.spanUnit) && p.startMonth.isBefore(current, this.spanUnit)) {
              project = p;
              truncate = -p.startMonth.diff(current, this.dateUnit);
              start = self.lowestMonth.clone();
              noStart = true;
            }
            // Same, but at the end
            if (project && project.endMonth.isAfter(this.highestMonth, this.spanUnit)) {
              truncate = project.endMonth.diff(this.highestMonth, this.dateUnit);
              noEnd = true;
            }
          })

          if (project) {
            span = project.endMonth.diff(project.startMonth, this.dateUnit)+1;
            span -= truncate;
            if (span <= 0) {
              span = 1; // do not let span equal 0 as it will cause infinite loop
            }
            progress = (project.progress * 100).toFixed(0);
            project.milestones.forEach(m => {
              const milestoneDay = moment(m.startTime)
              var startDay = moment(project.startTime).startOf('month')
              var closeDay = moment(project.closeTime).endOf('month')

              // Day span has to be clipped from each end to the visible range
              if (startDay.isBefore(this.lowestMonth.clone().startOf('month'))) {
                startDay = this.lowestMonth.clone().startOf('month')
              }
              if (closeDay.isAfter(this.highestMonth.clone().endOf('month'))) {
                closeDay = this.highestMonth.clone().endOf('month')
              }

              const daySpan = closeDay.diff(startDay, 'days')
              const daysFromStart = milestoneDay.diff(startDay, 'days')
              var position = (daysFromStart/Math.max(1, daySpan) * 100);

              // We need to hide milestones that are not within the visible range.
              // HOWEVER, it is possible for milestones to fall outside of the
              // project's start/end dates. We have to distinguish between:
              // 1 - milestones INDISDE the project range but not in visible range
              //     yet (we can scroll them into view later)
              // 2 - milestones OUTSIDE the project range (which are always outside
              //     of visible range)
              // We ignore 1
              // We show 2 as 0% or 100%
              
              if (daysFromStart < 0) {
                var projStart = moment(project.startTime).startOf('month');
                if (milestoneDay.isBefore(projStart, this.spanUnit) && projStart.isSameOrAfter(this.lowestMonth, this.spanUnit)) {
                  position = 0;
                } else {
                  return
                }
              }
              if (daysFromStart > daySpan) {
                var projClose = moment(project.closeTime).endOf('month');
                if (milestoneDay.isAfter(projClose) && projClose.isSameOrBefore(this.highestMonth, this.spanUnit)) {
                  position = 100;
                } else {
                  return
                }
              }

              milestones.push({
                id: m.uuId,
                position: position,
                name: m.name,
                date: milestoneDay.format('L'),
                progress: m.progress
              })
            })
          }
          filledRow.push({
            'date': this.formatDate(start),
            'span': span,
            'project': project,
            'noStart': noStart,
            'noEnd': noEnd,
            'progress': progress,
            'milestones': milestones
          });
          current = moment(current).add(span, self.dateUnit);
        }
        self.rows.push(filledRow);
        this.fetching = false;
      });
      
      Vue.nextTick(function () {
        // Project bars are first drawn on this tick!

        // Better UX if we force user to wait to load more or they might
        // hold the scroll bar and continue loading without intending to
        setTimeout(() => {
          self.attachListeners();
          self.canInteract = true;
          //self.checkTouchingEdge();
        }, 800);
        
      });
    },
    getOuterColor(progress, project) {
      if (this.display.coloring.project &&
          project.color) {
        return toComplimentary(project.color);
      }
      else if (this.display.coloring.customer &&
          project.customerColor && project.customerColor.length > 0 &&
          project.customerColor[0] !== "") {
        return toComplimentary(project.customerColor[0]);
      }
      else if (this.display.coloring.stage &&
          project.statusColor && project.statusColor.length > 0 &&
          project.statusColor[0] !== "") {
        return toComplimentary(project.statusColor[0]);
      }
      else if (this.display.coloring.tag &&
          project.tagColor && project.tagColor.length > 0 &&
          project.tagColor[0] !== "") {
        return toComplimentary(project.tagColor[0]);
      }
      else if (this.display.coloring.location &&
          project.locationColor && project.locationColor.length > 0 &&
          project.locationColor[0] !== "") {
        return toComplimentary(project.locationColor[0]);
      }
      
      if (this.display.coloring.none) {
        progress = (progress * 100).toFixed(0);
        if (progress == 0) {
          return "var(--timeline-0)";
        } else if (progress <= 25) {
          return "var(--timeline-25)";
        } else if (progress <= 50) {
          return "var(--timeline-50)";
        } else if (progress <= 75) {
          return "var(--timeline-75)";
        } else if (progress <= 99) {
          return "var(--timeline-99)";
        } else if (progress == 100) {
          return "var(--timeline-100)";
        }
      }
      return "var(--timeline-0)";
    },
    getTextColor(project) {
      if (this.display.coloring.project &&
          project.color) {
        return invertColor(project.color, true);
      }
      else if (this.display.coloring.customer &&
          project.customerColor && project.customerColor.length > 0 &&
          project.customerColor[0] !== "") {
        return invertColor(project.customerColor[0], true);
      }
      else if (this.display.coloring.stage &&
          project.statusColor && project.statusColor.length > 0 &&
          project.statusColor[0] !== "") {
        return invertColor(project.statusColor[0], true);
      }
      else if (this.display.coloring.tag &&
          project.tagColor && project.tagColor.length > 0 &&
          project.tagColor[0] !== "") {
        return invertColor(project.tagColor[0], true);
      }
      else if (this.display.coloring.location &&
          project.locationColor && project.locationColor.length > 0 &&
          project.locationColor[0] !== "") {
        return invertColor(project.locationColor[0], true);
      }
      return 'var(--black)';
    },
    getInnerColor(progress, project) {
      if (this.display.coloring.project &&
          project.color) {
        return project.color;
      }
      else if (this.display.coloring.customer &&
          project.customerColor && project.customerColor.length > 0 &&
          project.customerColor[0] !== "") {
        return project.customerColor[0];
      }
      else if (this.display.coloring.stage &&
          project.statusColor && project.statusColor.length > 0 &&
          project.statusColor[0] !== "") {
        return project.statusColor[0];
      }
      else if (this.display.coloring.tag &&
          project.tagColor && project.tagColor.length > 0 &&
          project.tagColor[0] !== "") {
        return project.tagColor[0];
      }
      else if (this.display.coloring.location &&
          project.locationColor && project.locationColor.length > 0 &&
          project.locationColor[0] !== "") {
        return project.locationColor[0];
      }
      
      if (this.display.coloring.none) {
        progress = (progress * 100).toFixed(0);
        if (progress == 0) {
          return "var(--timeline-0-light)";
        } else if (progress <= 25) {
          return "var(--timeline-25-light)";
        } else if (progress <= 50) {
          return "var(--timeline-50-light)";
        } else if (progress <= 75) {
          return "var(--timeline-75-light)";
        } else if (progress <= 99) {
          return "var(--timeline-99-light)";
        } else if (progress == 100) {
          return "var(--timeline-100-light)";
        }
      }
      return "var(--white)";
    },
    getNotchColor(progress, project) {
      if (this.display.coloring.project &&
          project.color) {
        return toComplimentary(project.color);
      }
      else if (this.display.coloring.customer &&
          project.customerColor && project.customerColor.length > 0 &&
          project.customerColor[0] !== "") {
        return toComplimentary(project.customerColor[0]);
      }
      else if (this.display.coloring.stage &&
          project.statusColor && project.statusColor.length > 0 &&
          project.statusColor[0] !== "") {
        return toComplimentary(project.statusColor[0]);
      }
      else if (this.display.coloring.tag &&
          project.tagColor && project.tagColor.length > 0 &&
          project.tagColor[0] !== "") {
        return toComplimentary(project.tagColor[0]);
      }
      else if (this.display.coloring.location &&
          project.locationColor && project.locationColor.length > 0 &&
          project.locationColor[0] !== "") {
        return toComplimentary(project.locationColor[0]);
      }
      
      
      if (this.display.coloring.none) {
        progress = (progress * 100).toFixed(0);
        if (progress == 0) {
          return "var(--progress-0)";
        } else if (progress <= 25) {
          return "var(--progress-25)";
        } else if (progress <= 50) {
          return "var(--progress-50)";
        } else if (progress <= 75) {
          return "var(--progress-75)";
        } else if (progress <= 99) {
          return "var(--progress-99)";
        } else if (progress == 100) {
          return "var(--progress-100)";
        }
      }
      return "var(--timeline-0)";
    },
    getColorClass(progress) {
      progress = (progress * 100).toFixed(0);
      if (progress == 0) {
        return "progress0";
      } else if (progress <= 25) {
        return "progress25";
      } else if (progress <= 50) {
        return "progress50";
      } else if (progress <= 75) {
        return "progress75";
      } else if (progress <= 99) {
        return "progress99";
      } else if (progress == 100) {
        return "progress100";
      }
    },
    saveSettings() {
      this.profile.display = this.display;
      this.$emit('saveWidget', this.profile);
    },
    onResize({width, height}) {
      if (!this.hasMounted) {
        return;
      }
      this.width = width;
      this.height = height;

      if (this.firstLoadDone) {
        // Sometimes resizing can move the scroll bar to the edge.
        // Treat these as normal scrolls that need more data on that ede
        //this.checkTouchingEdge();
      } else {
        this.firstLoadSetup();
      }
    },
    onColoringOver() {
      this.$refs.coloring.visible = true;
    },
    onColoringLeave() {
      this.$refs.coloring.visible = false;
    },
    onColorChange(val, color_key) {
      const coloring = this.display.coloring;
      for (const key of Object.keys(coloring)) {
        coloring[key] = false;
      }
      coloring[val] = true;
      this.display.coloring = coloring;
      this.saveSettings();
      this.fillTable(); // Need this for progress & togglers
    },
    setFilterFieldValues(data) {
      const customerList = [];
      const stageList = [];
      const projectList = [];
      const tagList = [];
      for (const project of data) {
        if (project.customer && -1 === customerList.findIndex(i => i.text === project.customer)) {
          customerList.push({ text: project.customer });
        }
        if (project.status && -1 === stageList.findIndex(i => i.text === project.status)) {
          stageList.push({ text: project.status });
        }
        if (project.name && -1 === projectList.findIndex(i => i.text === project.name)) {
          projectList.push({ text: project.name });
        }
        if (project.tag) {
          for (const tag of project.tag) {
            if (-1 === tagList.findIndex(i => i.text === tag)) {
              tagList.push({ text: tag });
            }
          }
        }
      }
      
      const valuesSortFunc = function (a, b) {
        return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
      };
      customerList.sort(valuesSortFunc);
      projectList.sort(valuesSortFunc);
      stageList.sort(valuesSortFunc);
      tagList.sort(valuesSortFunc);
      
      this.filterFieldValues = {
        customerName: customerList,
        projectName: projectList,
        stageName: stageList,
        tag: tagList
      };
      
      this.filterFields = [
        { value: 'customerName', text: this.$t('filter_component.customer_name') },
        { value: 'projectName', text: this.$t('filter_component.project_name') }, 
        { value: 'stageName', text: this.$t('filter_component.stage_name') }, 
        { value: 'tag', text: this.$t('filter_component.tag') }
      ];
      
      if (typeof this.profile.filterObject !== 'undefined') {
        this.filterObject = this.profile.filterObject;
      }
      if (typeof this.profile.filterHistory !== 'undefined') {
        this.filterHistory = this.profile.filterHistory;
      }
    },
    onFilterModified(filter) {
      this.profile.filterObject = filter;
      this.$emit('saveWidget', this.profile);
      // rebuild the filter lists to keep the checkboxes in sync
      this.setFilterFieldValues(this.projects);
      this.fillTable();
    },
    onHistoryModified(history) {
      this.profile.filterHistory = history;
      this.$emit('saveWidget', this.profile);
    },
    onViewOver() {
      this.$refs.view.visible = true;
    },
    onViewLeave(arg) {
      if (typeof arg?.toElement?.className === 'string' &&
          arg?.toElement?.className !== 'arrow' &&
          arg?.toElement?.className && !arg?.toElement?.className.startsWith('popover')) {
        this.$refs.view.visible = false;
      }
    },
    
    savePreset() {
      this.saveName = null;
      this.saveIndex = -1;
      this.saveProfile = { 
        type: 'timeline',
        sharingMembers: cloneDeep(this.userId),
        editingPermissions: cloneDeep(this.userId),
        display: cloneDeep(this.display),
        coloring: cloneDeep(this.coloring),
        filter: cloneDeep(this.filter),
        filterObject: cloneDeep(this.filterObject),
        startDate: cloneDeep(this.startDate),
        span: cloneDeep(this.span)
      };
      this.promptSaveShow = true;
    },
    async updateUsers(profile, updateUsers, service) {
      if (updateUsers) {
        const users = updateUsers.split(',');
        for (const user of users) {
          const profileData = await service.list(user)
           .then(response => {
             return response.data[response.data.jobCase];
          });
          const homeProfile = profileData.find(item => item.type === 'timeline');
          
          if (homeProfile) {
            homeProfile['display'] = profile.display;
            homeProfile['coloring'] = profile.coloring;
            homeProfile['filter'] = profile.filter;
            homeProfile['viewName'] = profile.name;
            homeProfile['filterObject'] = profile.filterObject;
            homeProfile['startDate'] = profile.startDate;
            homeProfile['span'] = profile.span;
            await service.update([homeProfile], user)
          }
        }
      }
    },
    confirmSaveOk({ profile, newDefault, updateUsers }) {
      if (newDefault) {
        // find the existing default view and turn it off
        const defaultView = this.timelineViews.find(v => v.defaultView);
        if (defaultView) {
          defaultView.defaultView = false;
          viewProfileService.updatePreset([defaultView], this.userId)
          .catch((e) => {
            console.error(e); // eslint-disable-line no-console
          });
        }
      }
      
      this.updateUsers(profile, updateUsers, dashboardProfileService);
      
      if (this.saveIndex !== -1) {
        this.timelineViews.splice(this.saveIndex, 1, profile);
      }
      else {
        this.addViews([profile]);
      }
      this.profile.timelineViews = this.timelineViews;
      this.$emit('saveWidget', this.profile);
    },
    updateColumnSettings(index, name, profile) {
      this.saveName = name;
      this.saveProfile = { 
        name: profile.name,
        uuId: profile.uuId,
        type: 'timeline',
        defaultView: profile.defaultView,
        sharingMembers: cloneDeep(profile.sharingMembers),
        editingPermissions: cloneDeep(profile.editingPermissions),
        display: cloneDeep(this.display),
        coloring: cloneDeep(this.coloring),
        filter: cloneDeep(this.filter),
        filterObject: cloneDeep(this.filterObject),
        startDate: cloneDeep(this.startDate),
        span: cloneDeep(this.span)
      };
      this.saveIndex = index;
      this.promptSaveShow = true;
    },
    copyColumnSettings(name, profile) {
      this.saveName = `${name} ${this.$t('dataview.copy_text')}`;
      this.saveProfile = { 
        name: `${name} ${this.$t('dataview.copy_text')}`,
        uuId: null,
        type: 'timeline',
        sharingMembers: cloneDeep(profile.userId),
        editingPermissions: cloneDeep(profile.userId),
        display: cloneDeep(profile.display),
        coloring: cloneDeep(profile.coloring),
        filter: cloneDeep(profile.filter),
        filterObject: cloneDeep(profile.filterObject),
        startDate: cloneDeep(profile.startDate),
        span: cloneDeep(profile.span)
      };
      this.saveIndex = -1;
      this.promptSaveShow = true;
    },
    shareColumnSettings(index, name, profile) {
      this.saveName = name;
      this.saveProfile = profile;
      this.saveIndex = index;
      this.promptShareShow = true;
    },
    removeColumnSettings(index) {
      this.confirmDeleteViewShow = true;
      this.deleteViewIndex = index;
    },
    confirmDeleteViewOk() {
      const toRemove = this.timelineViews.splice(this.deleteViewIndex, 1);
      
      viewProfileService.remove([{ uuId: toRemove[0].uuId }],
                        this.userId).then(() => {  
                        //
      })
      .catch((e) => {
        console.error(e); // eslint-disable-line no-console
      });
    },
    editPermission(view) {
      if (typeof view.editingPermissions === 'undefined') {
        return true;    
      }
      
      return view.editingPermissions.includes(this.userId);
    },
    addViews(views) {
      for (const view of views) {
        // if not in the list, add it
        if (view.type === 'timeline' &&
            this.timelineViews.findIndex((i) => i.uuId === view.uuId) === -1) {
          this.showInfo.push(false);
          this.timelineViews.push(view);
          if (this.profile.useDefault && view.defaultView) {
            this.loadViewSettings(view);
          }
        }
      }
      
      this.timelineViews.sort(function( a, b ) {
        if ( a.name.toLowerCase() < b.name.toLowerCase() ){
          return -1;
        }
        if ( a.name.toLowerCase() > b.name.toLowerCase() ){
          return 1;
        }
        return 0;
      });
    },
    loadViewSettings(view) {
      let dateChange = false;
      this.profile.display = this.display = cloneDeep(view.display);
      this.filter = this.profile.filter = cloneDeep(view.filter);
      this.profile.coloring = this.coloring = cloneDeep(view.coloring);
      this.profile.filterObject = this.filterObject = cloneDeep(view.filterObject);
      dateChange = this.startDate !== cloneDeep(view.startDate);
      this.profile.startDate = this.startDate = cloneDeep(view.startDate);
      this.profile.span = this.span = cloneDeep(view.span);
      this.viewName = this.profile.viewName = view.name;
      this.$emit('saveWidget', this.profile);
      
      // rebuild the filter lists to keep the checkboxes in sync
      this.setFilterFieldValues(this.projects);
      
      if (dateChange) {
        this.dateChanged();
      }
      else {
        this.fillTable();
      }
    },
    mobileCheck() {
      let check = false;
      //eslint-disable-next-line
      (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
      return check;
    },
    renderPopup(target) {
      this.popupRemoveMenu();
      this.$root.$emit('bv::hide::popover');
      const isMinimized = this.mobileCheck() || document.body.classList.contains('sidebar-minimized')
      let offsetTop = 0;
      let offsetLeft = 0;
      offsetTop = target.clientY - 600;
      offsetLeft = target.clientX - (isMinimized ? 200 : 350);
      if (offsetLeft < 200) {
        offsetLeft = 200;
      }
      target = target.target;
      const container = document.getElementById('timeline-wrapper');
      const rect = target.getBoundingClientRect();
      const left = offsetLeft + container.scrollLeft;
      let top = container.scrollTop;
      const containerHeight = container.offsetTop + container.offsetHeight;
      const menuItems = `<div class="staffpopupmenuoption ag-menu-option" tabindex="-1" role="treeitem" aria-level="1" data-action="goto-day">
                      <span ref="eIcon" class="ag-menu-option-icon" role="presentation"></span>
                      <span ref="eName" class="staffpopupmenutext">${this.$t('staff.overs_timescale_msg')}</span>
                  </div>`;
      
      const staffUsage = this.canView('TASK') && this.canView('STAFF') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="staff_usage">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.staff_usage') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const dashboard = this.canView('TASK') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="dashboard">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.dashboard') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const tasks = this.canView('TASK') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="tasks">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.tasks') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const gantt = this.canView('TASK') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="gantt">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.gantt') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const kanban = this.canView('TASK') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="board">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.board') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const resourceUsage = this.canView('TASK') && this.canView('RESOURCE') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="resource_usage">
            <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.menu.resource_usage') }</span>
            <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
      const divider = this.canView('TASK') ? `
        <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="none">
            <span ref="eIcon" class="separator-height separator-color ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
            <span ref="eName" class="separator-height separator-color ag-menu-option-part ag-menu-option-text"></span>
            <span ref="eShortcut" class="separator-height separator-color ag-menu-option-part ag-menu-option-shortcut"></span>
            <span ref="ePopupPointer" class="separator-height separator-color ag-menu-option-part ag-menu-option-popup-pointer"></span>
        </div>` : '';
                                                      
      const htmlContentStr = `
        <div class="projectal-context-menu ag-tabs ag-menu ag-focus-managed ag-ltr ag-popup-child" role="dialog" aria-label="Column Menu">
          
          <div role="presentation" class="ag-tabs-body ag-menu-body">
              <div class="padding-zero ag-menu-list ag-focus-managed" role="tree">
                  <div class="ag-tab-guard ag-tab-guard-top" role="presentation" tabindex="0"></div>
                  <div class="ag-menu-option cursor-pointer" tabindex="-1" role="treeitem" aria-level="1" data-action="edit-project">
                      <span ref="eIcon" class="ag-menu-option-part ag-menu-option-icon" role="presentation"></span>
                      <span ref="eName" class="ag-menu-option-part ag-menu-option-text popup-menu-font-size">${ this.$t('project.title_detail') }</span>
                      <span ref="eShortcut" class="ag-menu-option-part ag-menu-option-shortcut"></span>
                      <span ref="ePopupPointer" class="ag-menu-option-part ag-menu-option-popup-pointer"></span>
                  </div>
                  ${divider}
                  ${dashboard}
                  ${tasks}
                  ${gantt}
                  ${staffUsage}
                  ${kanban}
                  <div class="ag-tab-guard ag-tab-guard-bottom" role="presentation" tabindex="0"></div>
              </div>
          </div>
      </div>
      `;

      const cellMenu = this.htmlToElement(htmlContentStr.trim().replaceAll(/>\n[\s]+/g, '>'));
      cellMenu.style.left = `${left}px`;
      cellMenu.style.top = `${top}px`;
      let popupElement = null;
      popupElement = document.createElement('div');
      popupElement.classList.add('ag-theme-balham')
      popupElement.classList.add('ag-popup');
      popupElement.classList.add('header-group-column-popup');
      popupElement.appendChild(cellMenu);
      document.querySelector('#timeline-wrapper').appendChild(popupElement);

      //Register events for menu options      
      const menuOptions = cellMenu.querySelectorAll('.ag-menu-option');
      if (menuOptions != null) {
        for (let i = 0, len = menuOptions.length; i < len; i++) {
          //'Click'
          menuOptions[i].addEventListener('click', this.handleMenuOptionClick);
          //'Mouseenter' and 'Mouseleave'
          menuOptions[i].addEventListener('mouseenter', this.addMenuOptionHighlight);
          menuOptions[i].addEventListener('mouseleave', this.removeMenuOptionHighlight);
        }
      }

      //Register event for hiding menu when clicking outside of menu
      this.clickOutsideEventHandler = function(e) {
        const popupElem = document.querySelector('.header-group-column-popup');
        if (popupElem != null) {
          if (!popupElem.contains(e.target)) {
            popupElem.remove();
          }
        }
      }
      document.addEventListener('mousedown', this.clickOutsideEventHandler);
      
      this.popupElement = popupElement;
      popupElement.addEventListener('mouseleave', this.popupRemoveMenu);
    },
    popupRemoveMenu() {
        
      if (this.popupElement) {
        this.popupElement.removeEventListener('mouseleave', this.popupRemoveMenu);
        this.popupElement.remove();
        delete this.popupElement;
      }
      
    },
    handleMenuOptionClick(event) {
      const menuOption = event.target.closest('.ag-menu-option');
      const action = menuOption.dataset.action;
      if (action == 'edit-project') {
        this.$emit('editProject', this.projectId);
      } else if (action == 'dashboard') {
        this.$router.push(`/projects/summary/${this.projectId}`);
      } else if (action == 'tasks') {
        this.$router.push(`/projects/tasks/${this.projectId}`);
      } else if (action == 'gantt') {
        this.$router.push(`/projects/gantt/${this.projectId}`);
      } else if (action == 'staff_usage') {
        this.$router.push(`/projects/staff/${this.projectId}`);
      } else if (action == 'resource_usage') {
        this.$router.push(`/projects/resource/${this.projectId}`);
      } else if (action == 'board') {
        this.$router.push(`/projects/kanban/${this.projectId}`);
      }
      this.cleanUp();
    },
    addMenuOptionHighlight(event) {
      const menuOption = event.target.closest('.ag-menu-option');
      const activeClass = 'ag-menu-option-active';
      if (!menuOption.classList.contains(activeClass)) {
        menuOption.classList.add(activeClass);
      }
    },
    removeMenuOptionHighlight(event) {
      const menuOption = event.target.closest('.ag-menu-option');
      const activeClass = 'ag-menu-option-active';
      if (menuOption.classList.contains(activeClass)) {
        menuOption.classList.remove(activeClass);
      }
    },
    onInfoOver(index) {
      profileService.nodeList(this.timelineViews[index].uuId).then((response) => {
        this.timelineViews[index].owner = response.data.resultList.filter(v => this.timelineViews[index].editingPermissions.includes(v.uuId)).map(r => { return r.name }).join(", ");
        this.$set(this.showInfo, index, true);
      });
    },
    onInfoLeave(index) {
      this.$set(this.showInfo, index, false);
    },
    htmlToElement(html) {
      var template = document.createElement('template');
      html = html.trim(); // Never return a text node of whitespace as the result
      template.innerHTML = html;
      return template.content.firstChild;
    },
    handleMenuHeaderClick(/* event */) {
      this.cleanUp();
    },
    cleanUp() {
      if (this.clickOutsideEventHandler != null) {
        this.clickOutsideEventHandler({ target: null });
        document.removeEventListener('mousedown', this.clickOutsideEventHandler);
        this.clickOutsideEventHandler = null;
      }
    },
    applyHeaderClass(month) {
      let ret = month.isToday ? 'todayCol' : '';
      if (month.isSelected) {
        ret = `${ret} selectedCol`;
      }
      return ret;
    },
    isEllipsisActive(text, id) {
      return isEllipsisActive(text, this, id);
    },
    getSpanOptionLabel(value) {
      return this.spanOptions.find(i => i.value === value)?.text || value;
    },
  },
}
</script>

<style lang="scss" scoped>
  .controls {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    position: relative;
  }
  .search-container {
    margin: 0;
    margin-left: auto;
  }
  .legend-item {
    height: .8em;
    width: .8em;
    display: inline-block;
    margin-right: .8em;
  }
</style>

<style>
  .timeline-attribute-toggler {
    display: inline-block;
  }

  .timeline-attribute-toggler .btn {
    font-size: 18px;
    color: var(--grid-toolbar-button);
  }
  .timeline-attribute-toggler .dropdown ul {
    margin-top: 0;
  }

  .timeline-table {
    min-width: 100%;
    text-align: center;
    border-collapse: unset;
    border-spacing: 1px;
  }
  .todayCol, .todayHead {
    background: var(--today) !important;
  }
  .todayHead {
    border-top: 3px solid var(--status-red) !important;
  }

  .timeline-table th {
    text-align: inherit;
    font-size: 12px;
    color: var(--ag-header-foreground-color);
    background: var(--ag-header-background-color);
    height: 24px;
    border-top: 3px solid var(--timeline-header-border);
  }
  
  .timeline-table th, .timeline-table td {
    outline: 1px solid var(--ag-row-border-color);
    min-width: 80px;
    max-width: 80px;
    position: relative;
  }
  
  .timeline-table .project-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 12px;
    border-radius: 3px;
    color: var(--black);
    border-width: 1px;
    border-style: solid;
    position: relative;
    cursor: pointer;
  }
  
  .project-name {
    position: relative;
    overflow: hidden;
    flex-grow: 1;
    text-align: left;
    padding: 5px 5px 5px 10px;
    margin-left: 5px;
    cursor: pointer;
    user-select: none;
  }
  .progress-box {
    padding: 5px 0px;
    width: 35px;
    font-size: 12px;
    font-weight: 500;
    color: var(--white);
  }
  
  #timeline-wrapper {
    width: 100%;
    height: 100%;
    overflow: auto;
    position: relative;
  }

  /* Start or end not visible */
  .no-start .project-name {
    margin-left: 0;
  }
  .no-start.project-bar {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .no-end.project-bar {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .no-end .progress-box {
    display: none;
  }

  /* Popover colors and borders. */

   /* Headers */
  .timeline-tooltip.progress0 .timeline-tooltip-header {
    background-color: var(--timeline-0-light) !important;
  }
  .timeline-tooltip.progress25 .timeline-tooltip-header {
    background-color: var(--timeline-25-light) !important;
  }
  .timeline-tooltip.progress50 .timeline-tooltip-header {
    background-color: var(--timeline-50-light) !important;
  }
  .timeline-tooltip.progress75 .timeline-tooltip-header {
    background-color: var(--timeline-75-light) !important;
  }
  .timeline-tooltip.progress99 .timeline-tooltip-header {
    background-color: var(--timeline-99-light) !important;
  }
  .timeline-tooltip.progress100 .timeline-tooltip-header {
    background-color: var(--timeline-100-light) !important;
  }

  /* Borders */
  .timeline-tooltip.progress0 {
    border-color: var(--progress-0) !important;
  }
  .timeline-tooltip.progress25 {
    border-color: var(--progress-25) !important;
  }
  .timeline-tooltip.progress50 {
    border-color: var(--progress-50) !important;
  }
  .timeline-tooltip.progress75 {
    border-color: var(--progress-75) !important;
  }
  .timeline-tooltip.progress99 {
    border-color: var(--progress-99) !important;
  }
  .timeline-tooltip.progress100 {
    border-color: var(--progress-100) !important;
  }

  /* Arrows - before is border color, after is fill color */
  .progress0 .arrow:before {
    border-top-color: var(--progress-0) !important;
    border-bottom-color: var(--progress-0) !important;
  }
  .progress0 .arrow:after {
    border-bottom-color: var(--timeline-0-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }

  .progress25 .arrow:before {
    border-top-color: var(--progress-25) !important;
    border-bottom-color: var(--progress-25) !important;
  }
  .progress25 .arrow:after {
    border-bottom-color: var(--timeline-25-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }

  .progress50 .arrow:before {
    border-top-color: var(--progress-50) !important;
    border-bottom-color: var(--progress-50) !important;
  }
  .progress50 .arrow:after {
    border-bottom-color: var(--timeline-50-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }

  .progress75 .arrow:before {
    border-top-color: var(--progress-75) !important;
    border-bottom-color: var(--progress-75) !important;
  }
  .progress75 .arrow:after {
    border-bottom-color: var(--timeline-75-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }

  .progress99 .arrow:before {
    border-top-color: var(--progress-99) !important;
    border-bottom-color: var(--progress-99) !important;
  }
  .progress99 .arrow:after {
    border-bottom-color: var(--timeline-99-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }

  .progress100 .arrow:before {
    border-top-color: var(--progress-100) !important;
    border-bottom-color: var(--progress-100) !important;
  }
  .progress100 .arrow:after {
    border-bottom-color: var(--timeline-100-light) !important;
    border-top-color: var(--timeline-tooltip-bg) !important;
  }


  .timeline-tooltip-header {
    font-size: 15px;
    font-weight: 500;
    border-bottom: 1px solid var(--border-medium);
    padding-bottom: 4px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    color: var(--black) !important;
  }

  /* Tooltip content */
  .timeline-tooltip {
    visibility: hidden;
    width: 250px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    left: -4px !important;
  }
  .timeline-tooltip-body {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    background: var(--timeline-tooltip-bg);
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    color: var(--text-medium) !important;
  }
  .timeline-tooltip-body > div {
    margin: 4px 0;
  }

  .timeline-tooltip .popover-body {
    background-color: unset;
    padding: 0;
  }
  .timeline-tooltip-header, .timeline-tooltip-body {
    padding: .5rem .75rem;
  }
  .timeline-tooltip .customer {
    margin: 0;
  }
  .timeline-tooltip:first-child {
    margin-top: 0;
  }
  
  .timeline-tooltip  .title {
    font-weight: 700;
    font-size: 12px;
  }
  .timeline-tooltip  .content {
    font-size: 12px;
    color: var(--text-light);
  }
  .timeline-tooltip  .table {
    display: flex;
    flex-wrap: wrap;
    color: var(--text-medium) !important;
  }
  .timeline-tooltip  .table span {
    width: 50%;
  }

  /* Milestones */
  .milestone-tooltip-header {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .milestone-container {
    display: block;
    position: absolute;
    bottom: 0;
    left: 2px;
    width: calc(100% - 18px);
  }

  .diamond {
    margin-right: 10px;
    font-size: 18px;
  }

  .milestone-title {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
  .milestone-word {
    font-size: 12px;
    font-weight: 300;
  }
  .milestone-name {
    font-size: 14px;
  }

  .timeline-milestone {
    position: absolute;
    height: 10px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid var(--black);
    bottom: 2px;
    margin-left: 5px;
    margin-right: 2px;
  }
  .timeline-milestone .milestone-empty {
    position: absolute;
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
    border-bottom: 3px solid var(--black);
    bottom: -4px;
    left: -3px;
  }
  .timeline-tooltip.milestone {
    visibility: visible;
  }
  
  .center-text {
    text-align: center;
    margin: auto;
  }
  
  .controls .btn-secondary {
    background: transparent;
    border: none;
  }
  
  .timeline-buttons .dropdown.show>.dropdown-toggle {
    background-color: var(--grid-toolbar-dropdown-bg);
    color: var(--grid-toolbar-dropdown);
  }
  
  .no-left-padding .dropdown-menu .dropdown-item {
    padding: 4px 0;
  }
  
  .controls .btn.btn-secondary {
    color: var(--grid-toolbar-button);
  }
  
  .timeline-span {
    margin-bottom: 0;
    margin-top: 4px;
  }
  
  .controls .date-picker .btn-secondary {
    color: #212121;
    background-color: #E0E0E0;
  }
  
  .action-bar-dropdown .btn {
    margin-bottom: 2px;
  }
  
    
  .timeline-filter-flex-grow-container {
    min-width: 350px;
    margin-top: 2px;
  }
  
  @media only screen and (max-width: 500px) {
    .timeline-filter-flex-grow-container {
      min-width: 250px;
    }
  }

  .controls .list-filter-search-container {
    margin-left: 5px;
  }
  
  .timeline-priority-nav {
    display: flex;
    flex: 1;
  }
  
  .timeline-priority-nav-ul {
    display: flex !important;
    margin-bottom: 0;
    align-items: center;
  }
    
  .separator-color {
    border-bottom: 1px solid var(--ag-border-color);
  }
  
  .separator-height {
    line-height: 5px !important;
    padding: 0 !important;
  }
  
  .popup-menu-font-size {
    font-size: 14px;
  }
  
  .padding-zero {
    padding: 0;
  }
  
</style>